import React from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface AssignmentDataPoint {
  date: string;
  count: number;
}

interface AssignmentGraphProps {
  data: AssignmentDataPoint[];
  monthlyAverage: number;
}

export const AssignmentGraph: React.FC<AssignmentGraphProps> = ({ data, monthlyAverage }) => {
  const chartWidth = Platform.OS === 'web' ? 800 : Dimensions.get('window').width - 32;
  const chartHeight = 200;
  const barWidth = Math.max(20, (chartWidth / data.length) - 8);
  const maxValue = Math.max(...data.map(d => d.count), monthlyAverage, 1);
  
  const getBarHeight = (value: number) => {
    return (value / maxValue) * (chartHeight - 40);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="trending-up" size={24} color={theme.colors.primary} />
          <Text style={styles.title}>Total Assigned Vehicles Per Day (This Month)</Text>
        </View>
        <View style={styles.averageBadge}>
          <Ionicons name="stats-chart" size={16} color={theme.colors.blue600} />
          <Text style={styles.averageText}>Avg: {monthlyAverage.toFixed(1)}</Text>
        </View>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={Platform.OS === 'web'}>
        <View style={[styles.chartContainer, { width: Math.max(chartWidth, data.length * (barWidth + 8)) }]}>
          {/* Average Line */}
          <View 
            style={[
              styles.averageLine,
              { 
                bottom: getBarHeight(monthlyAverage) + 30,
                width: Math.max(chartWidth, data.length * (barWidth + 8))
              }
            ]}
          >
            <View style={styles.averageLineInner} />
          </View>

          {/* Bars */}
          <View style={styles.barsContainer}>
            {data.map((point, index) => {
              const barHeight = getBarHeight(point.count);
              const isAboveAverage = point.count >= monthlyAverage;
              
              return (
                <View key={index} style={styles.barGroup}>
                  <View style={styles.barWrapper}>
                    {/* Value Label */}
                    <Text style={[styles.valueLabel, { color: isAboveAverage ? theme.colors.green600 : theme.colors.textSecondary }]}>
                      {point.count}
                    </Text>
                    
                    {/* Bar */}
                    <View
                      style={[
                        styles.bar,
                        {
                          height: barHeight,
                          width: barWidth,
                          backgroundColor: isAboveAverage ? theme.colors.green600 : theme.colors.primaryLight,
                        },
                      ]}
                    />
                  </View>
                  
                  {/* Date Label */}
                  <Text style={styles.dateLabel}>{formatDate(point.date)}</Text>
                </View>
              );
            })}
          </View>
        </View>
      </ScrollView>

      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.colors.green600 }]} />
          <Text style={styles.legendText}>Above Average</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.colors.primaryLight }]} />
          <Text style={styles.legendText}>Below Average</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={styles.legendLine} />
          <Text style={styles.legendText}>Monthly Avg ({monthlyAverage.toFixed(1)})</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    ...theme.shadows.md,
    marginBottom: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.sm,
  },
  averageBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.gray100,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  averageText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as '600',
    color: theme.colors.blue600,
    marginLeft: 4,
  },
  chartContainer: {
    height: 240,
    position: 'relative',
    paddingTop: 20,
  },
  averageLine: {
    position: 'absolute',
    left: 0,
    height: 2,
    zIndex: 10,
  },
  averageLineInner: {
    height: '100%',
    borderTopWidth: 2,
    borderTopColor: theme.colors.blue600,
    borderStyle: 'dashed',
  },
  barsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 200,
    paddingTop: 20,
  },
  barGroup: {
    alignItems: 'center',
    marginHorizontal: 4,
  },
  barWrapper: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: 180,
  },
  bar: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minHeight: 2,
  },
  valueLabel: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '600' as '600',
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    marginTop: 4,
    transform: [{ rotate: '-45deg' }],
    width: 40,
    textAlign: 'center',
  },
  legend: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing.md,
    paddingTop: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    flexWrap: 'wrap',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: theme.spacing.md,
    marginBottom: theme.spacing.xs,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 6,
  },
  legendLine: {
    width: 20,
    height: 2,
    backgroundColor: theme.colors.blue600,
    marginRight: 6,
  },
  legendText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
});
